import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Api from '../Network';
import { message } from 'antd';
import { getErrorMessage } from '../utils/getErrorMessage';

export interface ProductFilters {
  name?: string;
  status?: string;
  isDeleted?: boolean;
}

export interface ProductPaging {
  page: number;
  pageSize: number;
}

export interface ProductSorting {
  [key: string]: 1 | -1;
}

export interface ProductListState {
  filters: ProductFilters;
  paging: ProductPaging;
  sorting: ProductSorting;
}

type ProductQueryOptions = {
  useQueryOptions?: any;
  baseURL?: string;
  initialFilters?: ProductListState;
  disableErrorHandling?: boolean;
};

export function useProducts(useListOptions?: ProductQueryOptions) {
  const [filters, setFilters] = useState<ProductListState>(() => {
    const initial = useListOptions?.initialFilters || {
      filters: { name: '' },
      paging: { page: 1, pageSize: 20 },
      sorting: {},
    };
    return {
      filters: { ...initial.filters },
      paging: { ...initial.paging },
      sorting: { ...initial.sorting },
    };
  });

  function setListFilters(path: string, value: any) {
    setFilters((filters) => {
      const newFilters = { ...filters } as any;
      const pathParts = path.split('.');
      let current = newFilters;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newFilters;
    });
  }

  useEffect(() => {
    setListFilters('paging', useListOptions?.initialFilters?.paging || { page: 1, pageSize: 20 });
  }, []); // Only run once on mount

  const queryResult = useQuery({
    queryKey: ['products', filters],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      // Check if we have authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      // Build query parameters for GET request
      const params = new URLSearchParams();

      // Add pagination
      params.append('page', filters.paging.page.toString());
      params.append('per-page', filters.paging.pageSize.toString());

      // Add all filters in Laravel QueryBuilder format
      Object.entries(filters.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // For products API, search should be sent as filter[name]
          if (key === 'search' && value) {
            params.append('filter[name]', value.toString());
          } else {
            params.append(`filter[${key}]`, value.toString());
          }
        }
      });

      // Add deleted filter (special case)
      if (filters.filters.isDeleted) {
        params.append('deleted', '1');
      }

      // Add sorting
      if (Object.keys(filters.sorting).length > 0) {
        const [field, direction] = Object.entries(filters.sorting)[0];
        params.append('sort', field);
        params.append('order', direction === 1 ? 'asc' : 'desc');
      }

      const url = `api/admin/products?${params.toString()}`;
      const response = await Api.get(url, {
        signal,
        baseURL: useListOptions?.baseURL,
      });

      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    ...useListOptions?.useQueryOptions,
  });

  useEffect(() => {
    if (queryResult.error && !useListOptions?.disableErrorHandling) {
      message.error(getErrorMessage(queryResult.error, 'Failed to load products'));
      console.error('Error fetching products:', queryResult.error);
    }
  }, [queryResult.error, useListOptions?.disableErrorHandling]);

  return {
    filters,
    setListFilters,
    ...queryResult,
  };
}
